import React from 'react';
import {
  Badge, Flex, Text, Tooltip, useColorModeValue,
} from '@chakra-ui/react';
import useCollectionsReminders, { nextRungDays } from '../hooks/useCollectionsReminders';
import useCompanyDefaults from '../hooks/useCompanyDefaults';
import { addDaysToSqlDate, invoiceAgeDays } from '../models/accountsReceivable';
import { SqlDateTimeToDisplay } from '../utils/SqlTools';

interface InvoiceReminderChipProps {
  invoiceId: string;
  customerId?: string;
  dateStr?: string;
}

// item 1163e (Chad) — the invoice page states its own dunning trail: whether the collections
// auto-reminder ladder has touched THIS invoice (latest rung + when), whether it's muted or
// its customer opted out, and roughly when the next reminder fires. Renders nothing for
// non-admins (the reminders feed is Accountant/Admin-gated and simply never loads) and for
// invoices the engine isn't watching. Managing mutes/sends stays on the Collections card.
const InvoiceReminderChip: React.FC<InvoiceReminderChipProps> = ({ invoiceId, customerId, dateStr }) => {
  const { reminders } = useCollectionsReminders();
  const { defaults } = useCompanyDefaults();
  const subColor = useColorModeValue('gray.500', 'whiteAlpha.600');

  if (!reminders?.config) return null;
  const termDays = defaults?.invoicePaymentTermDays ?? 30;
  const last = reminders.items.find((r) => r.targetId === invoiceId);
  const muted = reminders.mutedInvoices.includes(invoiceId);
  const optedOut = !!customerId && reminders.optedOutCustomers.includes(customerId);
  const on = reminders.config.enabled === true;

  // grandfather line — items already due when the engine was armed are never reminded
  const enabledAt = reminders.config.enabledAt ?? '';
  const due = addDaysToSqlDate(dateStr, termDays);
  const watched = on && !!enabledAt && !!due && due > enabledAt;

  // nothing to say: engine not watching this invoice and it has no trail
  if (!last && !watched) return null;

  let next = '';
  if (watched && !muted && !optedOut) {
    const nd = nextRungDays(reminders.config, invoiceAgeDays(dateStr) - termDays);
    if (nd !== null) next = addDaysToSqlDate(dateStr, termDays + nd);
  }

  return (
    <Flex align="center" wrap="wrap" sx={{ gap: '8px' }} mb={3}>
      <Text fontSize="xs" fontWeight="bold" letterSpacing="0.08em" color={subColor}>
        AUTO-REMINDERS
      </Text>
      {last && (
        <Tooltip label="The collections ladder's latest touch on this invoice (rung = days past due)" placement="top" hasArrow>
          <Badge colorScheme="purple" fontSize="10px" cursor="help">
            {`Reminded · rung ${last.step}d · ${SqlDateTimeToDisplay(last.sentAt)}`}
          </Badge>
        </Tooltip>
      )}
      {muted && <Badge fontSize="10px">MUTED — no auto-reminders for this invoice</Badge>}
      {optedOut && <Badge fontSize="10px">CUSTOMER OPTED OUT</Badge>}
      {!last && watched && !muted && !optedOut && (
        <Badge variant="outline" fontSize="10px">Watched by the collections ladder</Badge>
      )}
      {next && (
        <Tooltip label="When the ladder next advances for this invoice if it stays unpaid (approximate — sends within business hours). Mute or send-now lives on the accountant Collections card." placement="top" hasArrow>
          <Badge variant="outline" fontSize="10px" cursor="help">{`next ~${SqlDateTimeToDisplay(next)}`}</Badge>
        </Tooltip>
      )}
    </Flex>
  );
};

InvoiceReminderChip.defaultProps = {
  customerId: undefined,
  dateStr: undefined,
};

export default InvoiceReminderChip;
